// Custom Matomo Tag Manager loader using first-party proxy
// Replaces @openmost/nuxt-matomo to bypass ad blockers by loading from /mtm_assets/
export default defineNuxtPlugin(() => {
  if (import.meta.client) {
    const config = useRuntimeConfig();
    const matomoContainerId = config.public.matomo.containerId;
    
    if (matomoContainerId) {
      // Initialize Matomo Tag Manager array first
      window._mtm = window._mtm || [];
      
      // Initialize _paq array and configure POST method BEFORE container loads
      window._paq = window._paq || [];
      window._paq.push(['setRequestMethod', 'POST']);
      
      // Load from our own domain via proxy route instead of matomo.ullmax.com
      const url = `/mtm_assets/container_${matomoContainerId}.js`;
      
      // Inject script element
      const script = document.createElement('script');
      script.async = true;
      script.src = url;
      
      // Push MTM start event
      _mtm.push({ 
        'mtm.startTime': (new Date()).getTime(), 
        'event': 'mtm.Start' 
      });
      
      // Catch script loading errors
      script.onerror = () => {
        console.error('[Matomo] Failed to load container script:', url);
      };
      
      script.onload = () => {
        // Container check with retry mechanism
        let containerCheckAttempts = 0;
        const maxAttempts = 10;
        const checkInterval = 300;
        
        const configureContainers = () => {
          containerCheckAttempts++;
          
          const containersArray = window.MatomoTagManager?.containers;
          const hasContainers = containersArray && containersArray.length > 0;
          
          if (hasContainers) {
            // Success! Configure all containers to use our proxy
            window.MatomoTagManager.containers.forEach((container: any) => {
              if (container.matomoConfig) {
                container.matomoConfig.matomoUrl = window.location.origin;
                container.matomoConfig.trackingEndpoint = '/mtm_assets/t';
              }
            });
            
            console.log('[Matomo] ✓ Loaded successfully -', window.MatomoTagManager.containers.length, 'container(s) registered');
            return true;
          } else if (containerCheckAttempts >= maxAttempts) {
            // Failed after max attempts
            console.error('[Matomo] ✗ Failed to load - containers not registered after', maxAttempts * checkInterval, 'ms');
            return true;
          } else {
            // Retry
            setTimeout(configureContainers, checkInterval);
            return false;
          }
        };
        
        // Wait for script execution to complete before checking containers
        // Large scripts (183KB) need time to parse and execute after file loads
        const startContainerCheck = () => configureContainers();
        
        if ('requestIdleCallback' in window) {
          requestIdleCallback(startContainerCheck, { timeout: 500 });
        } else {
          setTimeout(startContainerCheck, 500);
        }
        
        // Configure Matomo to use POST and our tracking endpoint
        if (window._paq) {
          window._paq.push(['setRequestMethod', 'POST']);
          window._paq.push(['setTrackerUrl', '/mtm_assets/t']);
        }
        
        if (window.Matomo?.getAsyncTracker) {
          try {
            const tracker = window.Matomo.getAsyncTracker();
            tracker.setRequestMethod('POST');
            tracker.setTrackerUrl('/mtm_assets/t');
          } catch (e) {
            // Tracker not available yet
          }
        }
      };
      
      // Wait a moment before inserting script to avoid race conditions
      // GTM and other dependencies need time to initialize first
      const insertScript = () => {
        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript && firstScript.parentNode) {
          firstScript.parentNode.insertBefore(script, firstScript);
        } else {
          document.head.appendChild(script);
        }
      };
      
      // Small delay to let page initialize (fixes race condition with GTM)
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          setTimeout(insertScript, 50);
        });
      } else {
        setTimeout(insertScript, 50);
      }
    }
  }
});
